import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const VerificationADAR = ({ formikProps }) => {
  const { values } = formikProps;
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isPrimoAdherent, setIsPrimoAdherent] = useState(false);
  const [isEligible, setIsEligible] = useState(true);
  const [adhesionN1Data, setAdhesionN1Data] = useState(null);

  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const annee = values.annee;
        const nombreRuches = values.informationsApicoles?.nombreRuches || 0;

        // Vérifier si l'utilisateur a une adhésion ADAR N-1
        if (user && annee) {
          const token = localStorage.getItem('token');
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/adhesions/user`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

          const adhesionN1 = response.data.find(
            adh => adh.organisme === 'ADAR' && 
                   adh.annee === annee - 1 && 
                   (adh.status === 'actif' || adh.status === 'expiree')
          );

          const isPrimo = !adhesionN1;
          setIsPrimoAdherent(isPrimo);
          setAdhesionN1Data(adhesionN1 || null);

          // Si primo-adhérent et moins de 30 ruches : refus
          if (nombreRuches < 30) {
            setIsEligible(false);
          } else {
            setIsEligible(true);
          }
        } else {
          // Si pas connecté, on considère comme primo-adhérent
          setIsPrimoAdherent(true);
          if (nombreRuches < 30) {
            setIsEligible(false);
          } else {
            setIsEligible(true);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la vérification d\'éligibilité:', error);
      } finally {
        setLoading(false);
      }
    };

    checkEligibility();
  }, [user, values.annee, values.informationsApicoles?.nombreRuches]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-lg text-gray-600">Vérification en cours...</div>
      </div>
    );
  }

  const nombreRuches = values.informationsApicoles?.nombreRuches || 0;
  const nombreRuchesN1 = adhesionN1Data?.nombreRuches || 0;
  const etaitProfessionnelN1 = nombreRuchesN1 >= 60;

  // Déterminer le cas d'affichage
  const getEligibilityCase = () => {
    if (isPrimoAdherent) {
      if (nombreRuches < 30) return 'primo_non_eligible';
      if (nombreRuches >= 30 && nombreRuches < 60) return 'primo_30_60';
      if (nombreRuches >= 60) return 'primo_60_plus';
    } else {
      // Adhérent avec adhésion N-1
      if (etaitProfessionnelN1) {
        // Était professionnel (>= 60 ruches) en N-1
        if (nombreRuches < 30) return 'adherent_professionnel_non_eligible';
        if (nombreRuches >= 30 && nombreRuches < 60) return 'adherent_professionnel_30_60';
        if (nombreRuches >= 60) return 'adherent_60_plus';
      } else {
        // N'était pas professionnel en N-1
        if (nombreRuches < 30) return 'adherent_non_eligible';
        if (nombreRuches >= 30 && nombreRuches < 60) return 'adherent_30_60';
        if (nombreRuches >= 60) return 'adherent_60_plus';
      }
    }
    return 'primo_non_eligible';
  };

  const eligibilityCase = getEligibilityCase();

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Vérification d'éligibilité
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Vérification de vos critères d'adhésion à l'ADAR
        </p>
      </div>

      {isEligible ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-green-800">
                {isPrimoAdherent ? 'Vous êtes éligible à l\'adhésion à l\'ADAR.' : 'Vous êtes éligible au renouvellement de votre adhésion à l\'ADAR.'}
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p className="font-medium">Votre demande d'adhésion à l'ADAR peut être poursuivie.</p>

                {eligibilityCase === 'primo_30_60' && (
                  <p className="mt-2">
                    <strong>Primo-adhérent détecté :</strong> Vous avez {nombreRuches} ruches, ce qui répond au critère minimum de 30 ruches pour les nouveaux adhérents.
                  </p>
                )}
                
                {eligibilityCase === 'primo_60_plus' && (
                  <p className="mt-2">
                    <strong>Primo-adhérent détecté :</strong> Vous avez au moins 60 ruches, ce qui répond au critère minimum pour les nouveaux adhérents professionnels.
                  </p>
                )}
                
                {eligibilityCase === 'adherent_30_60' && (
                  <p className="mt-2">
                    <strong>Adhérent détecté :</strong> Vous avez {nombreRuches} ruches, ce qui répond au critère minimum de 30 ruches pour les adhérents en cours de professionalisation.
                  </p>
                )}
                
                {eligibilityCase === 'adherent_professionnel_30_60' && (
                  <p className="mt-2">
                    <strong>Adhérent détecté :</strong> Vous avez {nombreRuches} ruches, ce qui répond au critère minimum de 30 ruches pour les adhérents.
                  </p>
                )}
                
                {eligibilityCase === 'adherent_60_plus' && (
                  <p className="mt-2">
                    <strong>Adhérent détecté :</strong> Vous avez au moins 60 ruches, ce qui répond au critère minimum pour les adhérents professionnels.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-8 w-8 text-red-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-red-800">
                Adhésion refusée
              </h3>

              <div className="mt-2 text-sm text-red-700">
                <p>
                  Votre demande d'adhésion à l'ADAR ne peut pas être acceptée pour les raisons suivantes :
                </p>
                <ul className="mt-3 list-disc list-inside space-y-1">
              
                  {eligibilityCase === 'primo_non_eligible' && (
                    <>
                      <li>Vous êtes primo-adhérent (pas d'adhésion ADAR l'année précédente)</li>
                      <li>Vous avez déclaré <strong>{nombreRuches} ruche{nombreRuches > 1 ? 's' : ''}</strong></li>
                      <li>Le nombre minimum de ruches requis pour les primo-adhérents est de <strong>30 ruches</strong></li>
                    </>
                  )}
              
                  {eligibilityCase === 'adherent_non_eligible' && (
                    <>
                      <li>Vous avez déclaré <strong>{nombreRuches} ruche{nombreRuches > 1 ? 's' : ''}</strong></li>
                      <li>Le nombre minimum de ruches requis est de <b>30 ruches</b> pour les primo-adhérents en parcours de professionnalisation et de <b>60 ruches</b> pour les professionnels.</li>
                    </>
                  )}
                  
                  {eligibilityCase === 'adherent_professionnel_non_eligible' && (
                    <>
                      <li>Vous avez déclaré <strong>{nombreRuches} ruche{nombreRuches > 1 ? 's' : ''}</strong></li>
                      <li>Le nombre minimum de ruches requis pour les apiculteurs professionnels est de <b>60 ruches</b></li>
                    </>
                  )}
                </ul>
                <p className="mt-4">
                  Pour toute question, n'hésitez pas à nous contacter : <a href="mailto:adareunion974@gmail.com" className="text-red-800 underline hover:text-red-900">adareunion974@gmail.com</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Informations récapitulatives */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <h4 className="text-base font-medium text-gray-900 mb-4">Informations vérifiées</h4>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">Statut</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {eligibilityCase === 'primo_non_eligible' && 'Non éligible'}
              {eligibilityCase === 'primo_30_60' && 'Primo-adhérent'}
              {eligibilityCase === 'primo_60_plus' && 'Primo-adhérent'}
              {eligibilityCase === 'adherent_non_eligible' && "Renouvellement d'adhésion"}
              {eligibilityCase === 'adherent_30_60' && "Renouvellement d’adhésion (pour adhérent engagé dans un parcours de professionnalisation ou adhérent professionnel en baisse d'activité)"}
              {eligibilityCase === 'adherent_professionnel_non_eligible' && "Adhérent de l'année précédente"}
              {eligibilityCase === 'adherent_professionnel_30_60' && "Adhérent engagé dans un parcours de professionnalisation"}
              {eligibilityCase === 'adherent_60_plus' && "Renouvellement d'adhésion (professionnel)"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Nombre de ruches</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {nombreRuches} ruches
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Critère minimum</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {eligibilityCase === 'primo_non_eligible' && '30 ruches (primo-adhérent)'}
              {eligibilityCase === 'primo_30_60' && '30 ruches (parcours de professionnalisation)'}
              {eligibilityCase === 'primo_60_plus' && '60 ruches (professionnel)'}
              {eligibilityCase === 'adherent_non_eligible' && '30 ruches (parcours de professionnalisation) ou 60 ruches (professionnel)'}
              {eligibilityCase === 'adherent_30_60' && '30 ruches (parcours de professionnalisation)'}
              {eligibilityCase === 'adherent_professionnel_non_eligible' && '30 ruches'}
              {eligibilityCase === 'adherent_professionnel_30_60' && '30 ruches'}
              {eligibilityCase === 'adherent_60_plus' && '60 ruches (professionnel)'}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">Résultat</dt>
            <dd className="mt-1 text-sm font-semibold">
              <span className={isEligible ? 'text-green-600' : 'text-red-600'}>
                {isEligible ? '✓ Éligible' : '✗ Non éligible'}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      {/* Processus d'adhésion */}
      {isEligible && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h4 className="text-base font-medium text-blue-900 mb-3">
            Votre demande d'adhésion va suivre le processus suivant :
          </h4>
          <ul className="space-y-2 text-sm text-blue-800">
            {(eligibilityCase === 'primo_30_60' || eligibilityCase === 'primo_60_plus') && (
              <>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Soumission de votre demande d'adhésion</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Validation par le conseil d'administration de l'ADAR</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Réception d'un email avec le lien de paiement sécurisé</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Paiement en ligne par carte bancaire</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Activation de votre adhésion</span>
                </li>
              </>
            )}
            
            {eligibilityCase === 'adherent_30_60' && (
              <>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Examen de votre demande de renouvellement d'adhésion</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Validation par le conseil d'administration de l'ADAR</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Réception d'un email avec le lien de paiement sécurisé</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Paiement en ligne par carte bancaire</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Activation de votre adhésion</span>
                </li>
              </>
            )}
            
            {(eligibilityCase === 'adherent_60_plus' || eligibilityCase === 'adherent_professionnel_30_60') && (
              <>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Examen de votre demande de renouvellement d'adhésion</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Validation par l'ADAR</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Réception d'un email avec le lien de paiement sécurisé</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Paiement en ligne par carte bancaire</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Activation de votre adhésion</span>
                </li>
              </>
            )}
          </ul>
        </div>
      )}

      {/* Message d'information si non éligible */}
      {!isEligible && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-700">
            <strong>Note :</strong> Vous ne pouvez pas continuer votre adhésion. 
            Veuillez revenir à l'étape précédente pour modifier vos informations ou nous contacter pour plus d'informations.
          </p>
        </div>
      )}
    </div>
  );
};

export default VerificationADAR;
